import type { CSSProperties } from 'react';
import { strings } from '@/data/strings';
import { AnimatedBlueBackdrop } from '@/components/AnimatedBlueBackdrop/AnimatedBlueBackdrop';
import { useInView } from '@/hooks/useInView';
import styles from './ForProfessionals.module.css';

function cx(...parts: Array<string | false | null | undefined>) {
  return parts.filter(Boolean).join(' ');
}

const ICONS = [IconRadar, IconLayers, IconChat];

export function ForProfessionals() {
  const { ref: gridRef, inView: gridInView } = useInView<HTMLDivElement>({
    threshold: 0,
    rootMargin: '0px 0px -20% 0px',
  });

  const { ref: priceRef, inView: priceInView } = useInView<HTMLDivElement>({
    threshold: 0,
    rootMargin: '0px 0px -15% 0px',
  });

  return (
    <section id="para-profesionales" className={styles.section}>
      <AnimatedBlueBackdrop />

      <div className={cx('container', styles.inner)}>
        <header className={styles.header}>
          <p className={styles.eyebrow}>{strings.forProfessionals.eyebrow}</p>
          <h2 className={styles.title}>{strings.forProfessionals.title}</h2>
          <p className={styles.subtitle}>{strings.forProfessionals.subtitle}</p>
        </header>

        <div
          ref={gridRef}
          className={cx(styles.grid, gridInView && styles.revealed)}
        >
          {strings.forProfessionals.benefits.map((b, i) => {
            const Icon = ICONS[i];
            return (
              <article
                key={b.title}
                className={styles.card}
                style={{ '--i': i } as CSSProperties}
              >
                <div className={styles.iconWrap} aria-hidden="true">
                  <Icon />
                </div>
                <h3 className={styles.cardTitle}>{b.title}</h3>
                <p className={styles.cardBody}>{b.body}</p>
              </article>
            );
          })}
        </div>

        <div
          ref={priceRef}
          className={cx(styles.priceBox, priceInView && styles.priceRevealed)}
        >
          <p className={styles.priceLabel}>
            {strings.forProfessionals.pricing.label}
          </p>

          <p className={styles.priceLine}>
            <span className={styles.priceAmount}>
              {strings.forProfessionals.pricing.amount}
            </span>
            {strings.forProfessionals.pricing.period && (
              <span className={styles.pricePeriod}>
                {strings.forProfessionals.pricing.period}
              </span>
            )}
          </p>

          <p className={styles.pricePromo}>
            {strings.forProfessionals.pricing.promo}
          </p>

          <p className={styles.priceGuarantee}>
            {strings.forProfessionals.pricing.guarantee}
          </p>

          <a
            className={cx(styles.cta, priceInView && styles.ctaPulse)}
            href="#suscribirse"
          >
            <span>{strings.forProfessionals.pricing.cta}</span>
            <IconArrowRight />
          </a>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Icons — outline, currentColor. viewBox 32 para consistencia con WhatIs.
// ─────────────────────────────────────────────────────────────────────────────

const ICON_PROPS = {
  width: 28,
  height: 28,
  viewBox: '0 0 32 32',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

function IconRadar() {
  return (
    <svg {...ICON_PROPS} aria-hidden="true">
      <circle cx="16" cy="16" r="11" />
      <circle cx="16" cy="16" r="7" />
      <circle cx="16" cy="16" r="3" />
      <line x1="16" y1="16" x2="24" y2="8" />
    </svg>
  );
}

function IconLayers() {
  return (
    <svg {...ICON_PROPS} aria-hidden="true">
      <path d="M16 5 L27 11 L16 17 L5 11 Z" />
      <path d="M5 16 L16 22 L27 16" />
      <path d="M5 21 L16 27 L27 21" />
    </svg>
  );
}

function IconChat() {
  return (
    <svg {...ICON_PROPS} aria-hidden="true">
      <path d="M6 8 C6 6.3 7.3 5 9 5 L23 5 C24.7 5 26 6.3 26 8 L26 18 C26 19.7 24.7 21 23 21 L13 21 L8 26 L8 21 C7 21 6 20 6 19 Z" />
      <line x1="11" y1="11" x2="20" y2="11" />
      <line x1="11" y1="15" x2="17" y2="15" />
    </svg>
  );
}

function IconArrowRight() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="13 6 19 12 13 18" />
    </svg>
  );
}
